import { useCallback, useEffect } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import FormFieldset from '@/components/atoms/Fieldset/templates/FormFieldset';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import useDesks from '@/hooks/useDesks';
import { getPermissions } from '@/redux/permissions/selectors';
import { Desk } from '@/shared/types/desk';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { deskRules } from '@/util/validation/rules/desk-rules';

type Form = { keep_alive: string };
interface Props {
  desk?: Desk;
}

const KeepAliveFieldset = ({ desk }: Props) => {
  const { t } = useTranslation();
  const desk_id = desk?.desk_id;
  const { updateDesk, updateStatus } = useDesks(desk_id);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<Form>({ mode: 'onChange' });
  const onSubmit = useCallback<(form: Form) => void>(
    (form) => {
      updateDesk({ keep_alive: parseInt(form?.keep_alive, 10), desk_id: desk?.desk_id });
    },
    [desk?.desk_id, updateDesk]
  );

  const canWrite = useSelector((state: RootState) => getPermissions(state, 'desks', actions.WRITE));

  useEffect(() => {
    if (desk?.desk_id) {
      reset({ keep_alive: desk?.keep_alive?.toString() });
    }
  }, [desk?.keep_alive, desk?.desk_id, reset]);

  return (
    <FormFieldset
      title={t('environments.keep_alive')}
      subtitle={t('environments.keep_alive_subtitle')}
      primaryButton={{
        children: t('common.save'),
        isLoading: updateStatus === 'pending',
        disabled: !isDirty,
      }}
      onSubmit={handleSubmit(onSubmit)}
      isLoading={!desk}
      className="mb-8"
    >
      <div className="space-y-2">
        <Label htmlFor="keep_alive">{t('environments.seconds')}</Label>
        <Controller
          control={control}
          name="keep_alive"
          rules={deskRules(t).keepAlive}
          render={({ field }) => (
            <Input
              id="keep_alive"
              type="number"
              size="sm"
              readOnly={!canWrite}
              {...field}
              value={field.value ?? ''}
            />
          )}
        />
        {errors.keep_alive?.message && (
          <p className="text-sm text-destructive">{errors.keep_alive.message}</p>
        )}
      </div>
    </FormFieldset>
  );
};

export default KeepAliveFieldset;
